import { useMemo } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import { faEye } from '@fortawesome/pro-regular-svg-icons';
import { faHouse, faHouseCircleXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    deleteHouseholdById,
    disassociateContactsFromHousehold,
    updateHouseholdById,
} from 'api/households';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Table from 'components/table';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import { usePossibleCurrentContactHousehold } from 'customHooks/useCurrentContactHousehold';
import { accountQueryKeys } from 'queries/account-query-keys';
import { HouseholdContact } from 'types/domain';

const columnHelper = createColumnHelper<HouseholdContact>();

const ContactHouseholdTable = () => {
    const toast = useToast();
    const match = useRouteMatch();
    const history = useHistory();
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const { data: contact } = useCurrentContact();
    const { data: household } = usePossibleCurrentContactHousehold();
    const householdContacts = useMemo(() => household?.contacts ?? [], [household?.contacts]);

    const { mutate: deleteHousehold } = useMutation({
        mutationFn: () => deleteHouseholdById(accountId, household?.id as number),
        onSuccess: () => {
            toast.success(`Deleted household successfully!`);
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contactById(accountId, contact.id),
            });
        },
        onError: () => {
            toast.error(`Failed to delete household. Please try again.`);
        },
    });

    const { mutate: disassociateContact } = useMutation({
        mutationFn: (contactId: number) =>
            disassociateContactsFromHousehold(accountId, household?.id as number, [contactId]),
        onSuccess: () => {
            toast.success('Removed member from household successfully!');
            invalidate();
        },
        onError: () => {
            toast.error(`Failed to remove member from household. Please try again.`);
        },
    });

    const { mutate: makePrimaryContact } = useMutation({
        mutationFn: (contactId: number) =>
            updateHouseholdById(accountId, household!.id, { primaryContactId: contactId }),
        onSuccess: () => {
            toast.success('Updated household primary contact!');
            invalidate();
        },
        onError: () => {
            toast.error(`Failed to make member the primary contact. Please try again.`);
        },
    });

    const invalidate = async () => {
        queryClient.invalidateQueries({
            queryKey: accountQueryKeys.contactById(accountId, contact.id),
        });
        queryClient.invalidateQueries({
            queryKey: accountQueryKeys.householdById(accountId, household?.id as number),
        });
    };

    const columns = useMemo(
        () => [
            columnHelper.accessor('first_name', {
                id: 'contact_name',
                header: 'Member Name',
                cell: info => {
                    const householdContact = info.row.original;

                    return (
                        <div className={'flex items-center gap-2'}>
                            <p className={'font-medium'}>
                                {householdContact.first_name} {householdContact.last_name}
                            </p>
                            <div
                                className={'flex h-5 flex-grow items-center justify-between gap-2'}
                            >
                                <p
                                    className={`rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-500 ${
                                        householdContact.id === household?.head_contact_id
                                            ? 'visible'
                                            : 'not-sr-only invisible'
                                    }`}
                                >
                                    Primary contact
                                </p>
                                {householdContact.id === contact?.id ? (
                                    <div className="flex items-center gap-1 rounded-full bg-gray-50 px-2 py-1">
                                        <FontAwesomeIcon size={'sm'} icon={faEye} />
                                        <p className={'text-xs font-medium'}>Viewing</p>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    );
                },
            }),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const householdContact = info.row.original;
                    const isPrimaryContact = householdContact.id === household?.head_contact_id;
                    const isCurrentContact = contact?.id === householdContact.id;

                    if (isPrimaryContact && isCurrentContact) {
                        return null;
                    }

                    return (
                        <DropdownMenu.Root key={contact?.id}>
                            <Table.TableRowDropdownTrigger />
                            <DropdownMenu.Content sideOffset={5} align={'end'}>
                                <DropdownMenu.Item
                                    disabled={isCurrentContact}
                                    onClick={() =>
                                        history.push(
                                            `/accounts/${accountId}/contacts/${householdContact.id}`,
                                        )
                                    }
                                >
                                    View contact
                                </DropdownMenu.Item>
                                <AlertDialog.Root type={'error'}>
                                    <AlertDialog.Trigger asChild={true}>
                                        <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                                            Remove from household
                                        </DropdownMenu.Item>
                                    </AlertDialog.Trigger>
                                    <AlertDialog.Portal>
                                        <AlertDialog.Overlay />
                                        <AlertDialog.Content>
                                            <AlertDialog.Header>
                                                <AlertDialog.Icon />
                                                <AlertDialog.Close />
                                            </AlertDialog.Header>
                                            <AlertDialog.Body>
                                                <AlertDialog.Title>
                                                    Remove {householdContact.first_name} from
                                                    household
                                                </AlertDialog.Title>
                                                <AlertDialog.Description>
                                                    You won't be able to undo this action. Are you
                                                    sure?
                                                </AlertDialog.Description>
                                            </AlertDialog.Body>
                                            <AlertDialog.Footer>
                                                <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                                                <AlertDialog.Action
                                                    onClick={() =>
                                                        disassociateContact(householdContact.id)
                                                    }
                                                >
                                                    Remove from household
                                                </AlertDialog.Action>
                                            </AlertDialog.Footer>
                                        </AlertDialog.Content>
                                    </AlertDialog.Portal>
                                </AlertDialog.Root>
                                <DropdownMenu.Item
                                    disabled={isPrimaryContact}
                                    onClick={() => makePrimaryContact(householdContact.id)}
                                >
                                    Make primary contact
                                </DropdownMenu.Item>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    );
                },
            }),
        ],
        [accountId, contact, disassociateContact, history, household, makePrimaryContact],
    );

    const table = useReactTable({
        data: householdContacts,
        columns,
        enableSorting: false,
        getCoreRowModel: getCoreRowModel(),
        enableHiding: false,
        enableColumnPinning: false,
    });

    return (
        <Box className="overflow-hidden">
            <div className={'flex items-center justify-between p-4'}>
                <div className={'flex items-center gap-2'}>
                    <FontAwesomeIcon color={'text-gray-600'} icon={faHouse} />
                    <p className={'text-lg font-medium text-gray-700'}>
                        {household?.name ?? 'Household'}
                    </p>
                </div>
                {household ? (
                    <div className={'flex items-center gap-4'}>
                        <Button
                            onClick={() => history.push(`${match.url}/household/members/create`)}
                            variant={'outline'}
                            color={'neutral'}
                            size={'sm'}
                        >
                            + Add Member
                        </Button>
                        <DropdownMenu.Root>
                            <Table.TableRowDropdownTrigger />
                            <DropdownMenu.Content sideOffset={5} align={'end'}>
                                <DropdownMenu.Item
                                    onClick={() =>
                                        history.push(
                                            `${match.url}/household/${household.id}/update`,
                                        )
                                    }
                                >
                                    Edit household
                                </DropdownMenu.Item>
                                <AlertDialog.Root type={'error'}>
                                    <AlertDialog.Trigger asChild={true}>
                                        <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                                            Delete household
                                        </DropdownMenu.Item>
                                    </AlertDialog.Trigger>
                                    <AlertDialog.Portal>
                                        <AlertDialog.Overlay />
                                        <AlertDialog.Content>
                                            <AlertDialog.Header>
                                                <AlertDialog.Icon />
                                                <AlertDialog.Close />
                                            </AlertDialog.Header>
                                            <AlertDialog.Body>
                                                <AlertDialog.Title>
                                                    Delete household
                                                </AlertDialog.Title>
                                                <AlertDialog.Description>
                                                    You won't be able to undo this action. Are you
                                                    sure?
                                                </AlertDialog.Description>
                                            </AlertDialog.Body>
                                            <AlertDialog.Footer>
                                                <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                                                <AlertDialog.Action
                                                    onClick={() => deleteHousehold()}
                                                >
                                                    Delete
                                                </AlertDialog.Action>
                                            </AlertDialog.Footer>
                                        </AlertDialog.Content>
                                    </AlertDialog.Portal>
                                </AlertDialog.Root>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    </div>
                ) : (
                    <div className={'flex items-center gap-4'}>
                        <Button
                            size={'sm'}
                            onClick={() => history.push(`${match.url}/household/join`)}
                            variant={'outline'}
                            color={'neutral'}
                        >
                            Join Household
                        </Button>
                        <Button
                            variant="solid"
                            size={'sm'}
                            onClick={() => history.push(`${match.url}/household/create`)}
                        >
                            + Create New
                        </Button>
                    </div>
                )}
            </div>
            <Separator />
            <Table.Root variant={'bordered'} table={table}>
                <Table.Content>
                    <Table.Header />
                    <Table.Body />
                </Table.Content>
                <Table.Empty variant={'secondary'}>
                    <Table.EmptyIcon>
                        <FontAwesomeIcon icon={faHouseCircleXmark} />
                    </Table.EmptyIcon>
                    <Table.EmptyTitle>Not attached to household</Table.EmptyTitle>
                    <Table.EmptyDescription>
                        Add contact to an existing or new household
                    </Table.EmptyDescription>
                </Table.Empty>
            </Table.Root>
        </Box>
    );
};

export { ContactHouseholdTable };
